import { Component } from 'react';
import { RouteChildrenProps, withRouter } from 'react-router-dom';
import { FormattedMessage, IntlShape, injectIntl } from 'react-intl';
import { debounce, isNil } from 'lodash';
import { fromJS, List, RecordOf, Record } from 'immutable';
import mixpanel from 'mixpanel-browser';

// UTILS
import { getFilters, capitalizeFirstLetter, intercom } from 'utils';
import {
  addFiltersToStoreFromQueryParams,
  addFilterToQueryParams,
  Filter,
  FilterFactory,
  removeFilterFromQueryParams,
  setFiltersQueryParams,
} from './utils';

// CONSTANTS
import { FILTERED_ITEMS } from 'constants/filters';
import { FLASH_MESSAGE_TYPES, ENTITY_IDS_BY_NAME } from 'constants/common';
import { ANALYTICS_EVENTS } from 'constants/mixpanel.constants';

// HOCS
import withNotifications from 'hocs/Notifications';

// COMPONENTS
import { PrimaryButton } from 'components/Buttons';
import ExportViewModal from 'components/Modals/ExportView';
import Filters from 'components/Filters';
import FlashMessage from 'components/FlashMessage';
import Header from 'components/Header';
import ManageReportsModal from 'components/Modals/ManageReports';
import SaveViewModal from 'components/Modals/SaveView';
import RecurringEmailModal from 'components/Modals/RecurringEmail';
import Search from 'components/Search';
import Table from 'components/Table';
import Views from 'components/Views';
import ViewActions from 'components/Views/Actions';

// SELECTORS
import { selectErrors, selectNotifications } from 'services/Views/selectors';

// THUNKS
import { clearNotifications } from 'services/Views/thunks';
import { SelectedEntityType } from 'services/Hierarchy/actions';

// RESOURCES
import { HierarchyType } from 'services/resources';

// STYLES
import { FullSizeTableWrapper } from 'styles/table';
import { FilteredTableContainer, FiltersContainer, ContentContainer } from './FilteredTable.styles';

export type TableFieldRecord<T = object> = {
  name: string;
  dataPath: string | number | ((value: T, context?: any) => string | number);
  title: string | { translationId: string };
  decimalDigits?: number;
  deserialize?: (value: any) => any;
  disabled?: boolean;
  filterable?: boolean;
  filterValues?: List<any>;
  link?: string;
  linkAttributePath?: string;
  linkNewTab?: boolean;
  position?: number;
  serialize?: (value: any) => any;
  sortable?: boolean;
  type?: string;
  unitTranslations?: any;
  values?: any;
  valueTranslations?: any;
  hidden?: boolean;
  hierarchyOnly?: boolean;
  excludeOperators?: string[];
};

export const BASE_TABLE_FIELD = {
  name: '',
  dataPath: '',
  title: '',
  decimalDigits: undefined,
  deserialize: undefined,
  disabled: false,
  filterable: true,
  filterValues: undefined,
  link: undefined,
  linkAttributePath: undefined,
  linkNewTab: false,
  position: undefined,
  serialize: undefined,
  sortable: false,
  type: undefined,
  unitTranslations: undefined,
  values: undefined,
  valueTranslations: undefined,
  hidden: false,
  hierarchyOnly: false,
  excludeOperators: undefined,
} as const;

export const makeTableFieldRecord = Record<TableFieldRecord>(BASE_TABLE_FIELD);

const mergeColumns = (columnConfig, defaultColumns) => {
  const unhandledColumns = defaultColumns.filter((defaultColumn) => {
    const configuredColumn = columnConfig.find((column) => defaultColumn.get('field') === column.get('field'));

    if (configuredColumn) {
      return false;
    }

    return true;
  });

  let handledColumns = columnConfig
    .sortBy((fieldItem) => fieldItem.get('position'))
    .map((column, index) => column.set('position', index));

  let lastPosition = handledColumns.size;

  // Move the default columns that don't have position data to the end of the list
  unhandledColumns.forEach((column) => {
    let updatedColumn = column.set('position', lastPosition);
    updatedColumn = updatedColumn.set('disabled', true);
    handledColumns = handledColumns.push(updatedColumn);
    lastPosition += 1;
  });

  return handledColumns;
};

type PropsType<ItemType extends object = any> = RouteChildrenProps & {
  activeView: any;
  addNewFilter: (filter: any) => void;
  AlternateContent?: any;
  changeSelectionHandler?: (selection: List<ItemType>) => void;
  changePerPageValue: (perPage: number) => void;
  columns: any;
  defaultColumns: any;
  fieldContext?: any;
  fields: any;
  filters: any;
  getItems: (perPage: number, offset: number, filters: any, query?: any) => void;
  HeaderLeftItems?: any;
  HeaderRightItems?: any;
  headerTitleText: string;
  initialSort?: any;
  items: List<List<RecordOf<ItemType>>>;
  loading: boolean;
  notifications: any;
  offset: number;
  onSortBy?: (sortValue: string) => void;
  perPage: number;
  removeFilter: (index: number, filter: RecordOf<Filter>) => void;
  resetItems: () => void;
  searchQuery: string | undefined;
  searchPlaceholderText: string | undefined;
  numberOfResultsText: string | undefined;
  selectedHierarchyEntity: SelectedEntityType;
  setOffset: (offset: number) => void;
  setSearchQuery: (search: string) => void;
  setFilters: (filters: List<RecordOf<Filter>>) => void;
  setActiveView: (view: any) => void;
  showAlternateContent?: boolean;
  total: number;
  viewLoading: boolean;
  views: any;
  viewTypeName: keyof typeof FILTERED_ITEMS;
  // Intl
  intl: IntlShape;
};

type StateType = {
  columnConfig: any;
  editReport: any;
  filterSelected: boolean;
  flashMessage: any;
  flashMessageType: any;
  flashMessageVisible: boolean;
  saveAsNew: boolean;
  saveViewVisible: boolean;
  selectedItems: any;
  sortBy: any;
  exportViewModalVisible: boolean;
  manageReportsModalVisible: boolean;
  recurringEmailModalVisible: boolean;
  stashedProps: any;
};

export class FilteredTable extends Component<PropsType, StateType> {
  static defaultProps = {
    AlternateContent: null,
    fieldContext: undefined,
    HeaderLeftItems: null,
    HeaderRightItems: null,
    showAlternateContent: false,
    changeSelectionHandler: () => undefined,
    onSortBy: () => undefined,
    initialSort: null,
  };

  state = {
    columnConfig: this.props.columns.size
      ? mergeColumns(this.props.columns, this.props.defaultColumns)
      : this.props.defaultColumns,
    editReport: null,
    filterSelected: false,
    flashMessage: null,
    flashMessageType: null,
    flashMessageVisible: false,
    saveAsNew: false,
    saveViewVisible: false,
    selectedItems: fromJS([]),
    sortBy: this.props.initialSort,

    // MODALS
    exportViewModalVisible: false,
    manageReportsModalVisible: false,
    recurringEmailModalVisible: false,

    stashedProps: {
      activeView: this.props.activeView,
      columns: this.props.columns,
      filters: this.props.filters,
      perPage: this.props.perPage,
      viewLoading: this.props.viewLoading,
      views: this.props.views,
    },
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { stashedProps } = prevState;

    let newState = {
      columnConfig: prevState.columnConfig,
      stashedProps: {
        activeView: nextProps.activeView,
        columns: nextProps.columns,
        filters: nextProps.filters,
        perPage: nextProps.perPage,
        viewLoading: nextProps.viewLoading,
        views: nextProps.views,
      },
    };

    if (stashedProps.viewLoading && !nextProps.viewLoading) {
      newState = {
        ...newState,
        // @ts-expect-error TS(2322): Type '{ saveViewVisible: boolean; columnConfig: an... Remove this comment to see the full error message
        saveViewVisible: false,
      };
    }

    // Updating the columns based on the active view
    if (
      nextProps.columns.size > 0 &&
      !stashedProps.columns.equals(nextProps.columns) &&
      !nextProps.columns.equals(prevState.columnConfig)
    ) {
      newState.columnConfig = mergeColumns(nextProps.columns, nextProps.defaultColumns);
    }

    // Reset the default colums when returning to the "All" view
    if (stashedProps.activeView && !nextProps.activeView) {
      newState.columnConfig = nextProps.defaultColumns;
    }

    // Keep controlled selection in sync
    if (nextProps.selectedItems && !nextProps.selectedItems.equals(prevState.selectedItems)) {
      // @ts-expect-error TS(2339): Property 'setSelectedItems' does not exist on type 'Read... Remove this comment to see the full error message
      newState.selectedItems = nextProps.selectedItems;
    }

    return newState;
  }

  componentDidMount(): void {
    const filters = addFiltersToStoreFromQueryParams(this.props.location, this.props.setFilters);
    if (filters.length === 0 && !this.props.activeView) {
      this.getItems();
    }

    const immutableFilters = List(filters.map(FilterFactory));

    if (
      this.props.activeView &&
      (immutableFilters.size === 0 || this.props.activeView.get('rules').equals(immutableFilters))
    ) {
      const rules = this.props.activeView.get('rules');
      this.props.setFilters(rules);
      setFiltersQueryParams(rules, this.props.history);

      this.getItems();
    } else if (this.props.activeView && !this.props.activeView.get('rules').equals(immutableFilters)) {
      this.setActiveViewIfSimilarTo(immutableFilters);
      this.getItems(immutableFilters);
    } else if (this.props.filters.size > 0) {
      this.setActiveViewIfSimilarTo(this.props.filters);
    }
  }

  componentDidUpdate(prevProps: Readonly<PropsType>): void {
    const filtersHaveChanged = !prevProps.filters.equals(this.props.filters);
    const perPageHasChanged = prevProps.perPage !== this.props.perPage;
    const offsetHasChanged = prevProps.offset !== this.props.offset;
    const selectedHierarchyEntityHasChanged = prevProps.selectedHierarchyEntity !== this.props.selectedHierarchyEntity;
    const defaultColumnsHaveChanged = !prevProps.defaultColumns.equals(this.props.defaultColumns);

    // Ensure columnConfig is updated when defaultColumns change (i.e. when feature flags add or remove columns)
    if (defaultColumnsHaveChanged) {
      this.setState({
        columnConfig: this.props.columns.size
          ? mergeColumns(this.props.columns, this.props.defaultColumns)
          : this.props.defaultColumns,
      });
    }

    if (filtersHaveChanged || perPageHasChanged || offsetHasChanged || selectedHierarchyEntityHasChanged) {
      this.getItems();
    }

    if (this.props.filters && !this.props.activeView) {
      this.setActiveViewIfSimilarTo(this.props.filters);
    }

    if (!prevProps.items.equals(this.props.items)) {
      // Clear selection if items are changed.
      this.setState({ selectedItems: fromJS([]) });
      this.props.changeSelectionHandler(fromJS([]));
    }
  }

  componentWillUnmount = () => {
    this.props.setSearchQuery('');
  };

  getItems = (filters = null) => {
    const sortBy = this.props.initialSort || this.state.sortBy;
    const filtersForQuery = getFilters(filters !== null ? filters : this.props.filters, sortBy);
    this.props.getItems(
      this.props.perPage,
      this.props.offset,
      filtersForQuery,
      ...(this.props.searchQuery ? [this.props.searchQuery] : [])
    );
  };

  setActiveViewIfSimilarTo = (filters: List<RecordOf<Filter>>) => {
    for (const view of this.props.views) {
      const rulesMatchFilters = view.get('rules').every((rule) => {
        return filters.some((filter) => rule.keySeq().every((key) => filter.get(key) === rule.get(key)));
      });
      if (view.get('rules').size > 0 && view.get('rules').size === filters.size && rulesMatchFilters) {
        this.props.setActiveView(view);
        break;
      }
    }
  };

  getShareDropdownItems = () => [
    {
      translationId: 'views.Vehicles.vehiclesShareExportView',
      handleClick: this.handleToggleExportViewModal,
      disabled: !this.props.activeView,
    },
    // {
    //   translationId: 'views.Vehicles.vehiclesShareCreateRecurringEmail',
    //   handleClick: this.handleToggleRecurringEmailModal,
    // },
    // {
    //   translationId: 'views.Vehicles.vehiclesShareManageRecurringEmails',
    //   handleClick: this.handleToggleManageReportsModal,
    // },
  ];

  getVisibleFields = () =>
    this.props.fields.filter(
      (field) =>
        !field.get('hidden') &&
        (!field.get('hierarchyOnly') || this.props.selectedHierarchyEntity === HierarchyType.HIERARCHY)
    );

  // Gets the formatted columns including position and whether it should be displayed
  getTableFields = () =>
    this.getVisibleFields()
      .map((fieldItem) => {
        const column = this.state.columnConfig.find(
          (columnItem) => columnItem.get('field') === fieldItem.get('name'),
          null,
          fromJS({})
        );

        const formattedColumn = fieldItem.merge({
          position: column.get('position'),
          disabled: column.get('disabled'),
        });

        return fromJS(formattedColumn);
      })
      .filter((fieldItem) => !fieldItem.get('disabled'))
      .sortBy((fieldItem) => fieldItem.get('position'));

  // Changes the search query and gets results based on the query entered
  handleChangeSearchQuery = (value) => {
    if ((this.props.searchQuery === undefined || this.props.searchQuery === '') && value !== '') {
      mixpanel.track(ANALYTICS_EVENTS.SEARCH);
    }
    this.props.setSearchQuery(value);

    this.handleGetSearchedData(value);
  };

  // Searches using a debounce with the search query
  handleGetSearchedData = debounce((query) => {
    const filters = getFilters(this.props.filters, this.state.sortBy);

    this.props.getItems(this.props.perPage, this.props.offset, filters, query);
  }, 300);

  //=================================================
  //  Views related
  //=================================================

  // Indication that a filter is being added so we don't need to display the update / save view
  handleToggleFilterSelected = (selected = null) => {
    const isSelected = selected === null ? !this.state.filterSelected : selected;

    this.setState({ filterSelected: isSelected });
  };

  // Adds a new filter to the list
  handleAddFilter = (filter) => {
    this.handleToggleFilterSelected(false);
    addFilterToQueryParams(filter, this.props.history);
    this.props.addNewFilter(filter);
  };

  // Toggles a column visibility
  handleToggleColumnVisibility = (columnConfig) => {
    this.setState({ columnConfig });
  };

  // Applies the column changes
  handleApplyColumnChanges = (columnConfig) => {
    this.setState({ columnConfig });
    // @ts-expect-error TS(2339): Property 'setColumns' does not exist on type 'Read... Remove this comment to see the full error message
    this.props.setColumns(columnConfig);
  };

  // Cancels the column changes and sets them to the default ones
  handleCancelColumnChanges = () => {
    if (this.props.activeView && this.props.activeView.get('columns').size > 0) {
      this.setState({
        columnConfig: mergeColumns(this.props.activeView.get('columns'), this.props.defaultColumns),
      });
    } else {
      // @ts-expect-error TS(2339): Property 'setColumns' does not exist on type 'Read... Remove this comment to see the full error message
      this.props.setColumns([]);
      this.setState({ columnConfig: this.props.defaultColumns });
    }
  };

  //=================================================
  //  MODALS
  //=================================================

  // Toggles the export view modal
  handleToggleExportViewModal = () => {
    if (!this.props.activeView) {
      const messageId =
        this.props.views && this.props.views.size
          ? 'notification.selectAViewBeforeExport'
          : 'notification.createAndSelectAViewBeforeExport';

      this.handleOpenFlashMessage(
        this.props.intl.formatMessage({
          id: messageId,
        }),
        FLASH_MESSAGE_TYPES.warning
      );
      return;
    }

    this.setState({
      exportViewModalVisible: !this.state.exportViewModalVisible,
    });
  };

  // Displaying flash message when the view was exported
  handleExportViewSuccess = () => {
    this.handleOpenFlashMessage(
      this.props.intl.formatMessage({
        id: 'notification.viewExportedEmail',
      }),
      FLASH_MESSAGE_TYPES.success
    );
  };

  // Toggles the recurring email create/update modal
  handleToggleRecurringEmailModal = () => {
    this.setState({
      recurringEmailModalVisible: !this.state.recurringEmailModalVisible,
      editReport: null,
    });
  };

  // Displaying flash message when the view was saved
  handleSaveRecurringEmailSuccess = (name) => {
    this.handleOpenFlashMessage(
      this.props.intl.formatMessage({ id: 'notification.recurringEmailSaved' }, { name }),
      FLASH_MESSAGE_TYPES.success
    );
  };

  // Toggles the manage reports modal
  handleToggleManageReportsModal = () => {
    this.setState({
      manageReportsModalVisible: !this.state.manageReportsModalVisible,
    });
  };

  // Toggles the manage reports off and the recurring email on
  handleEditReportModal = (report) => {
    this.setState({
      editReport: report,
      recurringEmailModalVisible: true,
    });
  };

  // Defines whether the view can be saved / updated
  // @TODO: Move into ViewActions
  viewHasChanges = () => {
    // If a filter is being added, we won't display at all
    if (this.state.filterSelected) {
      return false;
    }

    const { activeView } = this.props;

    // If a view is active and changes are done to the filters or columns
    if (
      !isNil(activeView) &&
      (!activeView.get('rules').equals(this.props.filters) || !activeView.get('columns').equals(this.props.columns))
    ) {
      return true;
    }

    // If a view is active but no changes are done to the filters or columns
    if (this.props.activeView) {
      return false;
    }

    // If no view is active but new filters were added or columns were modified
    if (this.props.filters.size > 0 || this.props.columns.size > 0) {
      return true;
    }

    return false;
  };

  handleSaveAsNew = () => {
    this.setState({ saveViewVisible: true, saveAsNew: true });
  };

  // Toggles the save view modal
  // @TODO: Move into ViewActions
  handleToggleSaveViewModal = () => {
    this.setState({
      saveViewVisible: !this.state.saveViewVisible,
      saveAsNew: false,
      // viewName: this.props.activeView ? this.props.activeView.get('name') : '',
    });
  };

  /**
   * Cancels any staged filter and column changes. If a view is selected, only
   * the filters belonging to that view will still exist
   */
  // @TODO: Move into ViewActions
  handleCancelViewChanges = () => {
    setFiltersQueryParams(this.props.activeView?.get('rules') || [], this.props.history);
    // @ts-expect-error TS(2339): Property 'selectView' does not exist on type 'Read... Remove this comment to see the full error message
    this.props.selectView(this.props.activeView);
    this.handleCancelColumnChanges();
  };

  // Request to save a view
  // @TODO: Move into ViewActions
  handleSaveView = (viewName) => {
    if (this.props.activeView && !this.state.saveAsNew) {
      this.handleUpdateView();
    } else if (this.state.saveViewVisible && !isNil(viewName)) {
      this.handleCreateView(viewName);
    } else {
      this.handleToggleSaveViewModal();
    }
  };

  // Creates a new view
  // @TODO: Move into ViewActions
  handleCreateView = (viewName) => {
    this.props
      // @ts-expect-error TS(2339): Property 'createView' does not exist on type 'Read... Remove this comment to see the full error message
      .createView(
        viewName,
        this.props.filters.toJS(),
        this.props.columns.toJS(),
        // @ts-expect-error TS(2339): Property 'user' does not exist on type 'Readonly<{... Remove this comment to see the full error message
        this.props.user.id,
        ENTITY_IDS_BY_NAME[this.props.viewTypeName]
      )
      .then(() => {
        intercom.trackEvent('Created new view', {
          source: `${capitalizeFirstLetter(this.props.viewTypeName)} list`,
        });

        this.handleOpenFlashMessage(
          `${viewName} ${this.props.intl.formatMessage({
            id: 'notification.viewSaved',
          })}`,
          FLASH_MESSAGE_TYPES.success
        );
      });
  };

  // Updates the view
  // @TODO: Move into ViewActions
  handleUpdateView = () => {
    const { activeView } = this.props;

    if (!activeView) {
      return;
    }

    // @ts-expect-error TS(2339): Property 'updateView' does not exist on type 'Read... Remove this comment to see the full error message
    this.props.updateView(activeView.get('id'), this.props.filters.toJS(), this.props.columns.toJS()).then(() => {
      intercom.trackEvent('Updated view', {
        source: `${capitalizeFirstLetter(this.props.viewTypeName)} list`,
      });

      this.handleOpenFlashMessage(
        `${activeView.get('name')} ${this.props.intl.formatMessage({
          id: 'notification.viewUpdated',
        })}`,
        FLASH_MESSAGE_TYPES.success
      );
    });
  };

  //=================================================
  //  PAGINATION
  //=================================================

  // Gets the previous vehicles page
  handleGetPreviousPage = () => {
    // Getting the page number for what the offset would be if we switched pages
    if (this.props.items.get((this.props.offset - this.props.perPage) / this.props.perPage + 1)) {
      this.props.setOffset(this.props.offset - this.props.perPage);

      return;
    }

    this.props.getItems(
      this.props.perPage,
      this.props.offset - this.props.perPage,
      getFilters(this.props.filters, this.state.sortBy),
      this.props.searchQuery
    );
  };

  // Gets the next vehicles page
  handleGetNextPage = () => {
    // Getting the page number for what the offset would be if we switched pages
    if (this.props.items.get((this.props.offset + this.props.perPage) / this.props.perPage + 1)) {
      this.props.setOffset(this.props.offset + this.props.perPage);

      return;
    }

    this.props.getItems(
      this.props.perPage,
      this.props.offset + this.props.perPage,
      getFilters(this.props.filters, this.state.sortBy),
      this.props.searchQuery
    );
  };

  // Gets the specified page number
  handleGetSpecificPage = (page) => {
    // Getting the page number for what the offset would be if we switched pages
    if (this.props.items.get(((page - 1) * this.props.perPage) / this.props.perPage + 1)) {
      this.props.setOffset((page - 1) * this.props.perPage);

      return;
    }

    this.props.getItems(
      this.props.perPage,
      (page - 1) * this.props.perPage,
      getFilters(this.props.filters, this.state.sortBy),
      this.props.searchQuery
    );
  };

  // Changes the per page value
  handleChangePerPageValue = (perPage) => {
    this.props.changePerPageValue(perPage);
  };

  //=================================================
  //  SELECTION
  //=================================================

  // Marks an item as selected or unselects it if it was already selected
  handleToggleSelection = (item) => {
    const selectedItems = this.state.selectedItems;
    let newSelected;
    const index = selectedItems.findIndex((selected) => selected.get('id') === item.get('id'));
    if (index >= 0) {
      newSelected = selectedItems.delete(index);
    } else {
      newSelected = selectedItems.push(item);
    }

    this.setState({ selectedItems: newSelected });

    this.props.changeSelectionHandler(newSelected);
  };

  // Selects all or deselects all vehicles
  handleToggleSelectAll = () => {
    const selectedItems = this.state.selectedItems;
    const currentPageItems = this.props.items.get(this.props.offset / this.props.perPage + 1, fromJS([]));
    const newSelected = selectedItems.size !== currentPageItems.size ? currentPageItems : fromJS([]);

    this.setState({ selectedItems: newSelected });

    this.props.changeSelectionHandler(newSelected);
  };

  // Applies a sort on the query
  handleSortBy = (sortBy) => {
    this.setState({ sortBy });

    // this.props.resetItems();

    this.props.onSortBy(sortBy);

    this.props.getItems(
      this.props.perPage,
      this.props.offset,
      getFilters(this.props.filters, sortBy),
      this.props.searchQuery
    );
  };

  //=================================================
  //  FLASH MESSAGES
  //=================================================

  // Opens the flash message
  handleOpenFlashMessage = (text, type) => {
    this.setState({
      flashMessageVisible: true,
      flashMessageType: type,
      flashMessage: text,
    });
  };

  // Closes the flash message
  handleCloseFlashMessage = () => {
    this.setState({
      flashMessageVisible: false,
      flashMessageType: null,
      flashMessage: null,
    });
  };

  render() {
    const { showAlternateContent, AlternateContent } = this.props;

    return (
      <div>
        <FlashMessage
          visible={this.state.flashMessageVisible}
          type={this.state.flashMessageType}
          text={this.state.flashMessage}
          onClose={this.handleCloseFlashMessage}
        />

        {this.state.saveViewVisible && (
          <SaveViewModal
            // @ts-expect-error TS(2322): Type '{ loading: any; onCloseModal: () => void; on... Remove this comment to see the full error message
            loading={this.props.viewLoading}
            onCloseModal={this.handleToggleSaveViewModal}
            onSave={this.handleSaveView}
            initialViewName={this.props.activeView && !this.state.saveAsNew ? this.props.activeView.get('name') : ''}
          />
        )}

        <ExportViewModal
          // @ts-expect-error TS(2322): Type '{ visible: boolean; loading: any; view: any;... Remove this comment to see the full error message
          visible={this.state.exportViewModalVisible}
          loading={this.props.viewLoading}
          view={this.props.activeView}
          onExportSuccess={this.handleExportViewSuccess}
          onCloseModal={this.handleToggleExportViewModal}
        />

        <RecurringEmailModal
          // @ts-expect-error TS(2322): Type '{ visible: boolean; loading: any; report: an... Remove this comment to see the full error message
          visible={this.state.recurringEmailModalVisible}
          loading={this.props.viewLoading}
          report={this.state.editReport}
          views={this.props.views}
          onSaveSuccess={this.handleSaveRecurringEmailSuccess}
          onCloseModal={this.handleToggleRecurringEmailModal}
        />

        <ManageReportsModal
          // @ts-expect-error TS(2322): Type '{ visible: boolean; loading: any; onCloseMod... Remove this comment to see the full error message
          visible={this.state.manageReportsModalVisible && !this.state.recurringEmailModalVisible}
          loading={this.props.viewLoading}
          onCloseModal={this.handleToggleManageReportsModal}
          onEditReport={this.handleEditReportModal}
        />

        <Header title={this.props.headerTitleText}>
          <Search
            searchQuery={this.props.searchQuery}
            onChangeSearchQuery={this.handleChangeSearchQuery}
            placeholder={this.props.searchPlaceholderText}
          />
          {this.props.HeaderLeftItems}
          {/* <HeaderDropdown
            key="0"
            renderedValue={{ translationId: 'views.Vehicles.share' }}
            items={this.getShareDropdownItems()}
          /> */}
          <PrimaryButton
            key="0"
            onClick={() => {
              mixpanel.track(ANALYTICS_EVENTS.EXPORT_MODAL_OPENED);
              this.handleToggleExportViewModal();
            }}
          >
            <FormattedMessage id="buttons.export" />
          </PrimaryButton>
          {this.props.HeaderRightItems}
        </Header>

        <ContentContainer>
          <FiltersContainer>
            <Views
              // @ts-expect-error TS(2322): Type '{ views: any; activeView: any; columnConfig:... Remove this comment to see the full error message
              views={this.props.views}
              activeView={this.props.activeView}
              columnConfig={this.state.columnConfig}
              fields={this.getVisibleFields()}
              loading={this.props.loading}
              numberOfResults={this.props.total}
              numberOfResultsText={this.props.numberOfResultsText}
              onToggleColumnVisibility={this.handleToggleColumnVisibility}
              onApplyColumnsChanges={this.handleApplyColumnChanges}
              onCancelColumnsChanges={this.handleCancelColumnChanges}
              onSelectView={(view) => {
                setFiltersQueryParams(view?.get('rules') || [], this.props.history);
                // @ts-expect-error TS(2339): Property 'selectView' does not exist on type 'Read... Remove this comment to see the full error message
                this.props.selectView(view);
              }}
            />
            <Filters
              // @ts-expect-error TS(2769): No overload matches this call.
              type={FILTERED_ITEMS[this.props.viewTypeName]}
              fields={this.props.fields}
              filters={this.props.filters}
              onAddingFilter={this.handleToggleFilterSelected}
              onAddFilter={this.handleAddFilter}
              onRemoveFilter={(index, filter) => {
                removeFilterFromQueryParams(filter, this.props.history);
                this.props.removeFilter(index, filter);
              }}
            />
            <ViewActions
              visible={this.viewHasChanges()}
              loading={this.props.viewLoading}
              update={Boolean(this.props.activeView)}
              onCancel={this.handleCancelViewChanges}
              onSave={this.handleSaveView}
              onSaveAsNew={this.handleSaveAsNew}
            />
          </FiltersContainer>

          {!showAlternateContent && (
            <FilteredTableContainer>
              <FullSizeTableWrapper>
                <Table
                  // @ts-expect-error TS(2322): Type '{ fieldContext: any; currentPage: number; da... Remove this comment to see the full error message
                  fieldContext={this.props.fieldContext}
                  currentPage={this.props.offset / this.props.perPage + 1}
                  data={this.props.items.get(this.props.offset / this.props.perPage + 1, fromJS([]))}
                  fields={this.getTableFields()}
                  loading={this.props.loading}
                  perPage={this.props.perPage}
                  selectedItems={this.state.selectedItems}
                  sortBy={this.state.sortBy}
                  totalPages={Math.ceil(this.props.total / this.props.perPage)}
                  viewTypeName={this.props.viewTypeName}
                  onSortBy={this.handleSortBy}
                  onGetPreviousPage={this.handleGetPreviousPage}
                  onGetNextPage={this.handleGetNextPage}
                  onGetPage={this.handleGetSpecificPage}
                  onChangePerPage={this.handleChangePerPageValue}
                  onToggleSelection={this.handleToggleSelection}
                  onToggleSelectAll={this.handleToggleSelectAll}
                />
              </FullSizeTableWrapper>
            </FilteredTableContainer>
          )}
          {showAlternateContent &&
            AlternateContent({
              perPage: this.props.perPage,
              offset: this.props.offset,
              currentPage: this.props.offset / this.props.perPage + 1,
              totalPages: Math.ceil(this.props.total / this.props.perPage),
              onGetPreviousPage: this.handleGetPreviousPage,
              onGetNextPage: this.handleGetNextPage,
              onGetPage: this.handleGetSpecificPage,
              onChangePerPage: this.handleChangePerPageValue,
            })}
        </ContentContainer>
      </div>
    );
  }
}

const notificationsConfig = {
  selectNotifications,
  selectErrors,
  clearNotifications,
};

export default withNotifications(injectIntl(withRouter(FilteredTable)), notificationsConfig);
